// ============================================================
// useKillTeams - the kill-teams and their Cohesion, for every member
// (Squad Mode stage 6; plan modes-rework).
//
// The kill-teams are READ OFF THE ROSTER'S SHEETS (`killTeamsOf`): the team
// record each member carries holds the block and the leader. Two session
// documents: `direction/teams` (the GM's acting-leader answers,
// engine/killTeam/record) and `shared/cohesion` (the pools,
// engine/killTeam/pool). Every pool change is a COMPARE-AND-SET through
// `transact`: two brothers spending at once can never both spend the last
// point.
//
// The pools are NOT LOADED (null) until the server has answered; a team
// with no pool in what it answered has none - never a pool of 0 (gotcha
// 13). Echoes are filtered by their JSON, the way every synced document
// here is (gotcha 3).
//
// Without a session (a table alone in one browser) both live in this
// screen only.
// ============================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { emptyTeamsDoc, killTeamsOf, readTeamsDoc, type KillTeams, type TeamsDoc } from '../engine/killTeam/record';
import type { Combatant } from '../engine/roster/types';
import { emptyCohesionDoc, readCohesionDoc, type CohesionDoc } from '../engine/killTeam/pool';
import { diag, errorSummary } from '../diagnostics/log';
import type { SharedDocCallback } from '../multiplayer';

interface KillTeamSource {
  onTeamsDocUpdate(sessionId: string, callback: (doc: unknown | null) => void): () => void;
  setTeamsDoc(sessionId: string, doc: unknown): Promise<void>;
  onCohesionUpdate(sessionId: string, callback: SharedDocCallback<unknown>): () => void;
  transactCohesion(sessionId: string, mutate: (server: unknown | null) => unknown | undefined): Promise<unknown | null>;
  onStageUpdate(sessionId: string, callback: (stage: unknown) => void): () => void;
}

export interface KillTeamsState {
  /** Every kill-team on the roster, read off its sheets. */
  teams: KillTeams;
  /** The session's document: the GM's acting-leader answers. */
  doc: TeamsDoc;
  /** The pools; null while not loaded (never a pool of 0). */
  cohesion: CohesionDoc | null;
  /** Write the session's document (the GM's; the rules refuse anyone else). Nothing when unchanged. */
  setDoc(next: TeamsDoc): void;
  /**
   * Change the pools by compare-and-set: `mutate` gets the server's pools (an empty set when none yet) and returns
   * the next, or undefined to refuse. Resolves with what was written; null when nothing was.
   */
  transact(mutate: (doc: CohesionDoc) => CohesionDoc | undefined): Promise<CohesionDoc | null>;
  /** Put pools back as an undo snapshot held them. */
  impose(raw: unknown): void;
  /** The scene on stage (null with no scenes): an ability runs on one stage, and is joined only there. */
  sceneId: string | null;
}

/** A state that takes a new value only when its JSON differs: an echo of our own write changes nothing. */
function useJsonState<T>(initial: T) {
  const [value, setValue] = useState<T>(initial);
  const json = useRef(JSON.stringify(initial));
  const offer = useCallback((next: T) => {
    const text = JSON.stringify(next);
    if (text === json.current) return;
    json.current = text;
    setValue(next);
  }, []);
  return [value, offer] as const;
}

function useTeamsDoc(sessionId: string | null, mp: KillTeamSource) {
  const [doc, offerDoc] = useJsonState<TeamsDoc>(emptyTeamsDoc());
  useEffect(() => {
    if (!sessionId) return undefined;
    return mp.onTeamsDocUpdate(sessionId, (raw) => offerDoc(readTeamsDoc(raw)));
  }, [sessionId, mp, offerDoc]);
  // ONE WRITE AT A TIME, in the order sent: two answers in quick succession are two writes of the whole document, and on
  // PocketBase two in flight could land the older one last (the sweep's 4 after a 5).
  const queue = useRef<Promise<void>>(Promise.resolve());
  const setDoc = useCallback(
    (next: TeamsDoc) => {
      offerDoc(next);
      if (!sessionId) return;
      queue.current = queue.current
        .then(() => mp.setTeamsDoc(sessionId, next))
        .catch((err) => diag.error('sync', 'the kill-teams could not be saved', errorSummary(err)));
    },
    [sessionId, mp, offerDoc],
  );
  return { doc, setDoc };
}

function useCohesionDoc(sessionId: string | null, mp: KillTeamSource) {
  const [cohesion, offerCohesion] = useJsonState<CohesionDoc | null>(sessionId ? null : emptyCohesionDoc());
  const local = useRef<CohesionDoc>(emptyCohesionDoc());
  useEffect(() => {
    if (!sessionId) return undefined;
    // Loaded by the watch's first answer: a read that failed reaches its error path, never this one (gotcha 13). A
    // document not there yet - or unreadable, which `watchJsonDoc` reports as absent - is an empty set, so the GM's
    // screen fills it afresh; a team missing from it has no pool, never a pool of 0. (Read as `exists` it waited for a
    // write only Swear made: nothing filled a fresh session.)
    return mp.onCohesionUpdate(sessionId, (doc) => offerCohesion(readCohesionDoc(doc) ?? emptyCohesionDoc()));
  }, [sessionId, mp, offerCohesion]);
  const transact = useCallback(
    (mutate: (doc: CohesionDoc) => CohesionDoc | undefined) =>
      sessionId
        ? serverTransact(mp, sessionId, mutate, offerCohesion)
        : Promise.resolve(soloTransact(local, mutate, offerCohesion)),
    [sessionId, mp, offerCohesion],
  );
  return { cohesion, transact };
}

/** In a session: the step as a compare-and-set on the server's pools; what was written is shown at once. */
async function serverTransact(
  mp: KillTeamSource,
  sessionId: string,
  mutate: (doc: CohesionDoc) => CohesionDoc | undefined,
  offer: (doc: CohesionDoc) => void,
): Promise<CohesionDoc | null> {
  const written = await mp.transactCohesion(sessionId, (server) =>
    mutate(readCohesionDoc(server) ?? emptyCohesionDoc()),
  );
  const doc = readCohesionDoc(written);
  if (doc) offer(doc);
  return doc;
}

/** Alone at the table: the same step against this screen's own copy. */
function soloTransact(
  local: { current: CohesionDoc },
  mutate: (doc: CohesionDoc) => CohesionDoc | undefined,
  offer: (doc: CohesionDoc) => void,
): CohesionDoc | null {
  const next = mutate(local.current);
  if (!next) return null;
  local.current = next;
  offer(next);
  return next;
}

/** The scene on stage, as `shared/stage` says (the scenes keep it); null with none, or alone at the table. */
function useStageScene(sessionId: string | null, mp: KillTeamSource): string | null {
  const [sceneId, setSceneId] = useState<string | null>(null);
  useEffect(() => {
    // A new session (or none) starts with no scene until its own stage says one (the stage 7 review).
    setSceneId(null);
    if (!sessionId) return undefined;
    return mp.onStageUpdate(sessionId, (stage) =>
      setSceneId((stage as { sceneId?: string | null } | null)?.sceneId ?? null),
    );
  }, [sessionId, mp]);
  return sceneId;
}

interface Args {
  sessionId: string | null;
  mp: KillTeamSource;
  /** The roster, whose sheets say what the kill-teams are. */
  roster: readonly Combatant[];
}

export default function useKillTeams({ sessionId, mp, roster }: Args): KillTeamsState {
  const { doc, setDoc } = useTeamsDoc(sessionId, mp);
  // Derived on every roster change (a wound, a turn), but ONE IDENTITY while they read the same: every strip, meter
  // and lane re-renders only when a kill-team itself changes.
  const json = useMemo(() => JSON.stringify(killTeamsOf(roster, doc)), [roster, doc]);
  const teams = useMemo(() => JSON.parse(json) as KillTeams, [json]);
  const { cohesion, transact } = useCohesionDoc(sessionId, mp);
  const impose = useCallback(
    (raw: unknown) => {
      const doc = readCohesionDoc(raw);
      if (doc) void transact(() => doc);
    },
    [transact],
  );
  const sceneId = useStageScene(sessionId, mp);
  // One identity while nothing changes: the context it feeds re-renders its readers only on a change.
  return useMemo(
    () => ({ teams, doc, cohesion, setDoc, transact, impose, sceneId }),
    [teams, doc, cohesion, setDoc, transact, impose, sceneId],
  );
}
